import FeaturesPage from 'components/FeaturesPages/FeaturesPage'
import TakeBackYourInternetSection from 'components/HomePage/TakeBackYourInternetSection'
import React from 'react'
import { Text, Flex } from 'theme-ui'
import { Svg } from 'ui'
import FeaturesHeader from 'components/FeaturesPages/FeaturesHeader'
import FeaturesInfoBox from 'components/FeaturesPages/FeaturesInfoBox'
import FeaturesInstructionsBox, {
  FeaturesInstruction,
  FeaturesInstructionsBoxSection,
} from 'components/FeaturesPages/FeaturesInstructionsBox'
import TeleportPikaman from 'images/featuresPages/teleport-pikaman.svg'
import ChangeLocation from 'images/featuresPages/teleport-change-location.svg'
import TeleportStepOne from 'images/featuresPages/teleport-def-loc.svg'
import TeleportStepTwo from 'images/featuresPages/teleport-auto-mode.svg'
import TeleportStepThree from 'images/featuresPages/teleport-custom-location.svg'
import DefaultLocation from 'images/homePage/teleport.svg'
import { CONTROLDText } from 'ui/CONTROLD'
import FeaturesDivider from 'components/FeaturesPages/FeaturesDivider'
import { FeaturedProfileName } from 'components/Plans/FeaturedProfilesDropdown'
import TechnicalLimitationsSection from 'components/FeaturesPages/TechnicalLimitationsSection'
import { getDocumentHead } from 'components/DocumentHead'

const sectionsTitle = `Change locations with ${CONTROLDText} in 3 steps.`
const instructionTextOne = 'Navigate to the “Profile Options” section in your profile.'
const instructionTextTwo = 'Turn on the toggle to go into Automatic Mode (Best Performance).'
const instructionTextThree = 'Optionally choose a specific country you wish to appear in.'

export default function Teleport(): React.ReactElement {
  return (
    <FeaturesPage>
      <Flex sx={{ flexDirection: 'column', gap: '8rem', px: ['1.6rem', '2.4rem'] }}>
        <FeaturesHeader
          title="Teleport to other countries"
          FeatureImage={TeleportPikaman}
          featuredProfile={FeaturedProfileName.TELEPORT}
        />
        <FeaturesInfoBox
          title="Change your location"
          leftColumnContent={
            <Flex sx={{ flexDirection: 'column', gap: '2.4rem', fontSize: ['1.8rem', '2.1rem'] }}>
              <Text>
                You’ve probably heard of a VPN. One of the most popular reasons that a person might
                use a VPN is to appear as if they are in another country, either to redirect a
                specific service to another country, or for security reasons. In countries with
                limited freedom, this type of service can be essential for avoiding government
                surveillance.
              </Text>
              <Text>
                While a VPN is the more secure option for the latter use case (due to the
                limitations listed in the ‘Technical Stuff’ section below), if your use case is
                lower stakes (or you just have some other reason for wanting to seem like you’re
                elsewhere - we won’t judge), {CONTROLDText} can help.
              </Text>
            </Flex>
          }
          rightColumnContent={
            <Svg
              svg={ChangeLocation}
              sx={{
                flexShrink: 0,
                width: ['25.6rem', '40rem', '46.8rem'],
                height: ['25.6rem', '40rem', '46.8rem'],
              }}
              descriptiveText="Change location illustration"
            />
          }
          hasShadedBackground={true}
        />
        <FeaturesInfoBox
          title="How does Control D help?"
          leftColumnContent={
            <Flex
              sx={{
                height: ['25.6rem', '40rem', '46.8rem'],
              }}
            >
              <Svg
                svg={DefaultLocation}
                sx={{
                  flexShrink: 0,
                  width: ['25.6rem', '40rem', '46.8rem'],
                  height: ['25.6rem', '40rem', '46.8rem'],
                }}
                descriptiveText="Default location feature illustration"
              />
            </Flex>
          }
          rightColumnContent={
            <Text sx={{ my: 'auto', fontSize: ['1.8rem', '2.1rem'] }}>
              {CONTROLDText}’s Default Location feature is like a VPN without any apps. It will make
              sites that you visit think that you’re in a different country, and change your IP
              address from the perspective of websites and apps.
            </Text>
          }
          hasShadedBackground={false}
        />
        <FeaturesInstructionsBox sxContainer={{ display: ['flex', 'none', 'flex'] }}>
          <FeaturesInstructionsBoxSection
            title={<Text sx={{ maxWidth: '68rem', mx: 'auto' }}>{sectionsTitle}</Text>}
          >
            <FeaturesInstruction
              step={1}
              Illustration={TeleportStepOne}
              instructionText={instructionTextOne}
            />
            <FeaturesDivider />
            <FeaturesInstruction
              step={2}
              Illustration={TeleportStepTwo}
              instructionText={instructionTextTwo}
            />
            <FeaturesDivider />
            <FeaturesInstruction
              step={3}
              Illustration={TeleportStepThree}
              instructionText={instructionTextThree}
            />
          </FeaturesInstructionsBoxSection>
        </FeaturesInstructionsBox>
        <FeaturesInstructionsBox sxContainer={{ display: ['none', 'flex', 'none'] }}>
          <FeaturesInstructionsBoxSection
            title={<Text sx={{ maxWidth: '68rem', mx: 'auto' }}>{sectionsTitle}</Text>}
          >
            <FeaturesInstruction
              step={1}
              Illustration={TeleportStepOne}
              instructionText={instructionTextOne}
            />
            <FeaturesDivider />
            <FeaturesInstruction
              step={2}
              Illustration={TeleportStepTwo}
              instructionText={instructionTextTwo}
            />
          </FeaturesInstructionsBoxSection>
          <FeaturesInstructionsBoxSection>
            <FeaturesInstruction
              step={3}
              Illustration={TeleportStepThree}
              instructionText={instructionTextThree}
            />
          </FeaturesInstructionsBoxSection>
        </FeaturesInstructionsBox>

        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
          }}
        >
          <TechnicalLimitationsSection
            expandedContent={
              <>
                <Text sx={{ fontSize: ['1.8rem', '2.1rem'] }}>
                  Only works for Internet traffic that uses DNS - direct IP connections will go
                  direct.
                </Text>
                <Text sx={{ fontSize: ['1.8rem', '2.1rem'] }}>
                  May break protocols that don&apos;t support SNI.
                </Text>
                <Text sx={{ fontSize: ['1.8rem', '2.1rem'] }}>
                  ECH support is on the horizon, but until then, SNI is cleartext and so your ISP or
                  network administrator can still see what sites you visit if they perform Deep
                  Packet Inspection (DPI) on the network.
                </Text>
              </>
            }
          />
        </Flex>

        <TakeBackYourInternetSection />
      </Flex>
    </FeaturesPage>
  )
}

export const Head = getDocumentHead({
  title: 'Teleport',
  description:
    'Control D allows you to make sites that you visit think that you’re in a different country, and change your external IP address, all using the magic of DNS, without any VPN software.',
})
